import React, { cloneElement, createContext, isValidElement, useContext, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Tooltip as TooltipPrimitive } from '@base-ui/react/tooltip';

// Fluid Functionalism tooltip, cut down to Base UI. The bubble is an inverted
// chip: it fades and slides 4px toward the trigger. Motion, the weight
// animation, and the shape provider are left out.

const DEFAULT_DELAY = 200;
const SKIP_DELAY = 300;

const TooltipGroupContext = createContext(false);
const TravelGroupContext = createContext(null);

// One bubble for a row of triggers. It fades and scales in on the first
// hover, then travels between triggers (translate and width) while it stays
// open. Leaving the row hides it immediately. The bubble is portaled to the
// body, fixed to the row, so an overflow clip (the preview card) cannot cut
// it off. From transitions.dev "Tooltip open/close".
export function TooltipGroup({ children, className }) {
  const id = useId();
  const groupRef = useRef(null);
  const tipRef = useRef(null);
  const textRef = useRef(null);
  const anchorRef = useRef(null);
  const placeRef = useRef(null);

  const hide = (next) => {
    if (next && groupRef.current?.contains(next)) return;
    const tip = tipRef.current;
    if (!tip) return;
    anchorRef.current = null;
    tip.setAttribute('data-show', 'false');
    tip.setAttribute('aria-hidden', 'true');
  };

  const place = (trigger, content, { animate } = {}) => {
    const tip = tipRef.current;
    const text = textRef.current;
    const group = groupRef.current;
    if (!tip || !text || !group) return;
    anchorRef.current = { trigger, content };
    const showing = tip.getAttribute('data-show') === 'true';
    const travel = animate ?? showing;
    text.textContent = typeof content === 'string' || typeof content === 'number' ? String(content) : '';
    const theme = group.closest('[data-theme]')?.getAttribute('data-theme');
    if (theme) tip.setAttribute('data-theme', theme);
    else tip.removeAttribute('data-theme');
    const width = Math.ceil(text.offsetWidth);
    const groupBox = group.getBoundingClientRect();
    const triggerBox = trigger.getBoundingClientRect();
    const x = triggerBox.left + triggerBox.width / 2 - width / 2;
    if (!travel) tip.style.transition = 'none';
    tip.style.width = `${width}px`;
    tip.style.setProperty('--edl-tooltip-x', `${x}px`);
    tip.style.setProperty('--edl-tooltip-y', `${groupBox.top}px`);
    if (!travel) {
      void tip.offsetWidth;
      tip.style.transition = '';
    }
    tip.setAttribute('data-show', 'true');
    tip.setAttribute('aria-hidden', 'false');
  };
  placeRef.current = place;

  useEffect(() => {
    const follow = () => {
      const anchor = anchorRef.current;
      if (!anchor || tipRef.current?.getAttribute('data-show') !== 'true') return;
      placeRef.current(anchor.trigger, anchor.content, { animate: false });
    };
    window.addEventListener('scroll', follow, true);
    window.addEventListener('resize', follow);
    return () => {
      window.removeEventListener('scroll', follow, true);
      window.removeEventListener('resize', follow);
    };
  }, []);

  const bubble = (
    <span
      ref={tipRef}
      id={id}
      className="edl-tooltip-group__bubble"
      role="tooltip"
      aria-hidden="true"
      data-show="false"
    >
      <span ref={textRef} className="edl-tooltip-group__text" />
    </span>
  );

  return (
    <TravelGroupContext.Provider value={{ id, place, hide }}>
      <span
        ref={groupRef}
        className={['edl-tooltip-group', className].filter(Boolean).join(' ')}
        onPointerLeave={(event) => hide(event.relatedTarget)}
      >
        {children}
      </span>
      {typeof document === 'undefined' ? bubble : createPortal(bubble, document.body)}
    </TravelGroupContext.Provider>
  );
}

export function TooltipProvider({
  children,
  delayDuration = DEFAULT_DELAY,
  skipDelayDuration = SKIP_DELAY,
}) {
  return (
    <TooltipGroupContext.Provider value={true}>
      <TooltipPrimitive.Provider delay={delayDuration} timeout={skipDelayDuration}>
        {children}
      </TooltipPrimitive.Provider>
    </TooltipGroupContext.Provider>
  );
}

export function Tooltip({
  content,
  children,
  side = 'top',
  sideOffset = 8,
  delayDuration,
  forceOpen,
  onOpenChange,
  className,
  contentClassName,
}) {
  const hasAmbientProvider = useContext(TooltipGroupContext);
  const travel = useContext(TravelGroupContext);

  if (travel && isValidElement(children)) {
    return cloneElement(children, {
      'aria-describedby': travel.id,
      onPointerEnter: (event) => {
        children.props.onPointerEnter?.(event);
        travel.place(event.currentTarget, content);
      },
      onFocus: (event) => {
        children.props.onFocus?.(event);
        travel.place(event.currentTarget, content);
      },
      onBlur: (event) => {
        children.props.onBlur?.(event);
        travel.hide(event.relatedTarget);
      },
    });
  }


  const tooltip = (
    <TooltipPrimitive.Root
      {...(forceOpen !== undefined ? { open: forceOpen } : {})}
      onOpenChange={(next) => onOpenChange?.(next)}
    >
      <TooltipPrimitive.Trigger
        render={children}
        {...(hasAmbientProvider && delayDuration !== undefined ? { delay: delayDuration } : {})}
      />
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Positioner
          className={['edl-tooltip__positioner', contentClassName].filter(Boolean).join(' ')}
          side={side}
          sideOffset={sideOffset}
        >
          <TooltipPrimitive.Popup className={['edl-tooltip__popup', 'edl-tooltip__bubble', className].filter(Boolean).join(' ')}>
            {content}
          </TooltipPrimitive.Popup>
        </TooltipPrimitive.Positioner>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );

  if (hasAmbientProvider) return tooltip;

  return (
    <TooltipPrimitive.Provider delay={delayDuration ?? DEFAULT_DELAY}>
      {tooltip}
    </TooltipPrimitive.Provider>
  );
}
